// City registry — single source of truth for Pawground's SEO landing pages.
//
// Two-file design:
//   - cities.featured.json: hand-curated cities (Tampa Bay + PR top markets)
//     with unique blurbs, taglines, and lat/lng for richer JSON-LD.
//   - cities.templated.json: the population-driven expansion pack (484 US
//     cities). Each entry is just [name, state]; blurbs and slugs are
//     synthesised at module-load time so the data file stays compact.
//
// URL slug format: "{city-name-kebab}-{state-lower}"   (e.g. "tampa-fl")
// New URL pattern: /{city-slug}                        (e.g. /tampa-fl)
// New URL pattern: /{category-slug}/{city-slug}        (e.g. /dog-parks/tampa-fl)
// Old (back-compat): /fl/tampa, /fl/tampa/dog-parks    (still resolve;
//                                                       canonical → new URL)

import featuredData from "../data/cities.featured.json";
import templatedData from "../data/cities.templated.json";

// Reserved root-level slugs — never resolve as city pages (would collide
// with /search, /login, etc.). The router pre-checks against this set.
export const RESERVED_SLUGS = new Set([
  "search", "login", "signup", "profile", "blog", "guides", "place", "park",
  "fl", "pr", "api", "auth", "manifest.json", "sw.js", "sitemap.xml",
  "robots.txt", "favicon.ico", "icon.svg",
  "forgot-password", "reset-password", "onboarding", "privacy", "terms",
  // /private-parks/:slug routes are claimed by PrivateParkDetail.
  "private-parks",
  // Category hub URLs — must not be matched by the /:citySlug catch-all.
  "dog-parks", "dog-beaches", "dog-friendly-restaurants",
  "vets", "groomers", "boarding", "trainers", "pet-stores", "trails",
  // category slugs themselves are reserved — they're the first segment of
  // /{category}/{city-slug} routes.
  "dog-parks", "dog-beaches", "dog-friendly-restaurants", "groomers", "vets",
  "boarding", "trainers", "pet-stores", "trails",
]);

// Region/country lookup for templated cities (only US states represented
// in the templated list). PR is featured-only, so always present in
// featured data with country=PR.
const REGION_BY_STATE = {
  AK: "Alaska", AL: "Alabama", AR: "Arkansas", AZ: "Arizona", CA: "California",
  CO: "Colorado", CT: "Connecticut", DC: "District of Columbia", DE: "Delaware",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", IA: "Iowa", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", KS: "Kansas", KY: "Kentucky", LA: "Louisiana", MA: "Massachusetts",
  MD: "Maryland", ME: "Maine", MI: "Michigan", MN: "Minnesota", MO: "Missouri",
  MS: "Mississippi", MT: "Montana", NC: "North Carolina", ND: "North Dakota",
  NE: "Nebraska", NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico",
  NV: "Nevada", NY: "New York", OH: "Ohio", OK: "Oklahoma", OR: "Oregon",
  PA: "Pennsylvania", PR: "Puerto Rico", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VA: "Virginia",
  VT: "Vermont", WA: "Washington", WI: "Wisconsin", WV: "West Virginia", WY: "Wyoming",
};

// Convert a city display name to the URL slug component:
//  "St. Petersburg" → "st-petersburg"
//  "Coeur d'Alene"  → "coeur-dalene"
//  "Lee's Summit"   → "lees-summit"
//  "Winston-Salem"  → "winston-salem"
const kebab = (s) =>
  s
    .toLowerCase()
    .normalize("NFD").replace(/\p{Diacritic}/gu, "")  // strip accents
    .replace(/[''.]/g, "")                            // drop apostrophes + periods
    .replace(/[^a-z0-9]+/g, "-")                      // non-alphanum → hyphen
    .replace(/^-+|-+$/g, "");

// Three blurb variants — rotated deterministically by a small hash on the
// slug so each city consistently uses the same one (stable across builds)
// while the templated set as a whole has variety. Avoids Google's
// duplicate-content flag for the 480+ templated cities while keeping
// the data layer compact.
const TEMPLATE_VARIANTS = [
  (name, region) =>
    `${name} is one of ${region}'s growing dog-friendly communities. Pawground helps local pet parents find dog parks, pet-friendly restaurants, vets, groomers, boarders, trainers, pet stores, and trails — with real check-ins and reviews from the people who actually take their pups there.`,
  (name, region) =>
    `Dog parents in ${name}, ${region} have more options every year — neighborhood parks, vet clinics, groomers, dog-friendly patios, hiking trails, and more. Pawground brings them all together in one map so you can plan the weekend, find an emergency vet, or just discover a new spot down the road.`,
  (name, region) =>
    `Whether you're new to ${name} or you've lived in ${region} for years, finding genuinely dog-friendly spots takes time. Pawground combines fresh Google listings with on-the-ground check-ins, reviews, and live busy reports from real local pet parents so you can pick the right place before you go.`,
];

const variantIndex = (slug) => {
  // Tiny djb2-style hash → stable choice of 0/1/2 per slug.
  let h = 5381;
  for (let i = 0; i < slug.length; i++) h = ((h * 33) ^ slug.charCodeAt(i)) >>> 0;
  return h % TEMPLATE_VARIANTS.length;
};

const templateBlurb = (name, region, slug) =>
  TEMPLATE_VARIANTS[variantIndex(slug)](name, region);

// Assemble templated entries into the same shape as featured. They share
// every field so downstream consumers (CityPage, prerender, JSON-LD)
// don't branch.
const templated = templatedData.map(([name, state]) => {
  const region = REGION_BY_STATE[state] || state;
  const slug = `${kebab(name)}-${state.toLowerCase()}`;
  return {
    slug,
    name,
    state,
    region,
    country: state === "PR" ? "PR" : "US",
    query: `${name}, ${state}`,
    lat: null,
    lng: null,
    tagline: null,
    templated: true,
    blurb: templateBlurb(name, region, slug),
  };
});

// Featured entries take precedence — drop any templated duplicates by slug.
const featuredSlugs = new Set(featuredData.cities.map((c) => c.slug));
const dedupedTemplated = templated.filter((c) => !featuredSlugs.has(c.slug));

export const CITIES = [...featuredData.cities, ...dedupedTemplated];

// Quick-lookup index by slug. Built once at module load.
const CITY_BY_SLUG = new Map(CITIES.map((c) => [c.slug.toLowerCase(), c]));

// --- Lookup helpers -----------------------------------------------------

/** Lookup by the new flat slug ("tampa-fl"). Returns null on miss. */
export const getCityBySlug = (slug) => {
  if (!slug) return null;
  if (RESERVED_SLUGS.has(slug.toLowerCase())) return null;
  return CITY_BY_SLUG.get(slug.toLowerCase()) || null;
};

/** Legacy lookup: /fl/tampa style. Used to keep old URLs resolving. */
export const getCity = (stateCode, citySlugBase) => {
  if (!stateCode || !citySlugBase) return null;
  const combined = `${citySlugBase.toLowerCase()}-${stateCode.toLowerCase()}`;
  return CITY_BY_SLUG.get(combined) || null;
};

/** New canonical URL for a city. */
export const cityUrl = (city) => `/${city.slug}`;

/** New canonical URL for a city × category combo. */
export const cityCategoryUrl = (city, categoryUrlSlug) =>
  `/${categoryUrlSlug}/${city.slug}`;

/** Old URL form (kept resolving for back-compat); returns "/fl/tampa". */
export const oldCityUrl = (city) => {
  // Strip "-{state}" suffix from slug to get the legacy base.
  const base = city.slug.replace(new RegExp(`-${city.state.toLowerCase()}$`), "");
  return `/${city.state.toLowerCase()}/${base}`;
};

// --- Internal linking helpers -------------------------------------------

/** Cities in the same state, excluding the input city. Used for the
 *  "Nearby cities" row on city hubs and the "Same category in nearby
 *  cities" row on category pages. We rank by featured-first (curated
 *  blurbs are higher-value links), then alphabetical for stability. */
export const getNearbyCitiesInState = (city, limit = 6) => {
  if (!city) return [];
  return CITIES
    .filter((c) => c.state === city.state && c.slug !== city.slug)
    .sort((a, b) => {
      // Featured (non-templated) first.
      const at = a.templated ? 1 : 0;
      const bt = b.templated ? 1 : 0;
      if (at !== bt) return at - bt;
      return a.name.localeCompare(b.name);
    })
    .slice(0, limit);
};

/** Resolve a place's city from its backend-populated `park.city` field
 *  (provided by google_places.py). Returns the city object or null if
 *  we don't have a hub page for that city. */
export const cityFromPlace = (park) => {
  if (!park?.city || !park?.state) return null;
  const stateLower = park.state.toLowerCase();
  // First try the exact slug ({kebab(city)}-{state}).
  const slug = `${park.city
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[''.]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")}-${stateLower}`;
  return CITY_BY_SLUG.get(slug) || null;
};

/** All cities in a given state (no limit). Used by the city hub's
 *  collapsible "All {region} cities" mega-menu — a single page that
 *  internally links to every state-mate hub, accelerating Google's
 *  crawl + indexing of the long tail. */
export const getAllCitiesInState = (city) => {
  if (!city) return [];
  return CITIES
    .filter((c) => c.state === city.state)
    .sort((a, b) => {
      const at = a.templated ? 1 : 0;
      const bt = b.templated ? 1 : 0;
      if (at !== bt) return at - bt;
      return a.name.localeCompare(b.name);
    });
};

/** Top 10 cities surfaced in the global footer. FL-weighted to match
 *  Pawground's launch market. Each entry resolves to a real city in
 *  the registry — if you add a city here, ensure it exists in the
 *  featured or templated data. */
export const FOOTER_TOP_CITIES = [
  "tampa-fl",
  "st-petersburg-fl",
  "miami-fl",
  "orlando-fl",
  "jacksonville-fl",
  "riverview-fl",
  "brandon-fl",
  "wesley-chapel-fl",
  "san-juan-pr",
  "bayamon-pr",
]
  .map((slug) => CITY_BY_SLUG.get(slug))
  .filter(Boolean);
